<template>
  <div class="h-full overflow-auto flex-1 flex flex-col">
    <woot-loading-state
      v-if="uiFlags.isFetchingItem"
      :message="$t('MACROS.EDITOR.LOADING')"
    />
    <macro-form
      v-if="macro && !uiFlags.isFetchingItem"
      :macro-data.sync="macro"
      :is-submitting="uiFlags.isCreating || uiFlags.isUpdating"
      @submit="saveMacro"
    />
  </div>
</template>

<script>
import MacroForm from './MacroForm';
import { MACRO_ACTION_TYPES } from './constants';
import { mapGetters } from 'vuex';
import actionQueryGenerator from 'dashboard/helper/actionQueryGenerator.js';
import { matchOptionsByIds } from 'dashboard/helper/automationHelper';
import alertMixin from 'shared/mixins/alertMixin';
import macrosMixin from 'dashboard/mixins/macrosMixin';

export default {
  components: {
    MacroForm,
  },
  mixins: [alertMixin, macrosMixin],
  provide() {
    return {
      macroActionTypes: this.macroActionTypes,
    };
  },
  data() {
    return {
      macro: null,
      mode: 'CREATE',
      macroActionTypes: MACRO_ACTION_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'macros/getUIFlags',
      labels: 'labels/getLabels',
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
      currentUser: 'getCurrentUser',
      inboxes: 'inboxes/getAllWhatsAppTemplates',
      emailTemplates: 'emailTemplates/getEmailTemplates',
    }),
    macroId() {
      return this.$route.params.macroId;
    },
  },
  watch: {
    $route: {
      handler() {
        this.fetchDropdownData();
        if (this.$route.params.macroId) {
          this.fetchMacro();
        } else {
          this.initNewMacro();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchDropdownData() {
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('inboxes/refetch');
      this.$store.dispatch('emailTemplates/get');
    },
    fetchMacro() {
      this.mode = 'EDIT';
      this.manifestMacro();
    },
    async manifestMacro() {
      await this.$store.dispatch('macros/getSingleMacro', this.macroId);
      const singleMacro = this.$store.getters['macros/getMacro'](this.macroId);
      this.macro = this.formatMacro(singleMacro);
    },
    formatMacro(macro) {
      const formattedActions = macro.actions.map(action => {
        let actionParams = [];
        if (action.action_params?.length) {
          const matchedAction = this.macroActionTypes.find(
            item => item.key === action.action_name
          );
          const inputType = matchedAction ? matchedAction.inputType : null;

          if (inputType === 'multi_select' || inputType === 'search_select') {
            const options = this.getDropdownValues(
              action.action_name,
              this.$store
            );
            actionParams = matchOptionsByIds(options, action.action_params);

            if (action.action_name === 'whatsapp_template') {
              actionParams.forEach(template => {
                if (
                  String(template.id) !== String(action.action_params[0]) ||
                  !template.components
                ) {
                  return;
                }
                const customComponents = [
                  {
                    header_handle: action.action_params[2],
                    text: action.action_params[1],
                    format: action.action_params[3] || null,
                  },
                ];
                template.components = customComponents;
              });
            }
            if (action.action_name === 'email_template') {
              const ep = action.action_params;
              actionParams.forEach(t => {
                if (String(t.id) !== String(ep[0])) return;
                if (ep.length >= 2 && ep[1] !== undefined && ep[1] !== null) {
                  t.subject = ep[1] === '' ? t.subject : ep[1];
                }
                if (ep.length >= 3 && ep[2] !== undefined && ep[2] !== null) {
                  t.body = ep[2] === '' ? t.body : ep[2];
                }
              });
              // Multiselect + editors expect a single object, not [{ ... }] (same as new macro).
              if (actionParams.length > 0) {
                actionParams = { ...actionParams[0] };
              }
            }
          } else if (inputType === 'team_message') {
            const source = Array.isArray(action.action_params)
              ? action.action_params[0]
              : action.action_params;
            actionParams = {
              team_ids: matchOptionsByIds(
                this.getDropdownValues(action.action_name, this.$store),
                source?.team_ids
              ),
              message: source?.message ?? '',
            };
          } else if (inputType === 'api_webhook') {
            // For api_webhook, action_params is an object with type, url, method, headers, custom_instructions
            if (action.action_params && typeof action.action_params === 'object' && !Array.isArray(action.action_params)) {
              actionParams = { ...action.action_params };
            } else if (Array.isArray(action.action_params) && action.action_params[0]) {
              actionParams = { ...action.action_params[0] };
            } else {
              actionParams = {
                url: '',
                method: 'POST',
                headers: '',
                payload: '',
                auto_execute: false,
              };
            }
          } else if (inputType === 'kivo_webhook') {
            if (action.action_params && typeof action.action_params === 'object' && !Array.isArray(action.action_params)) {
              actionParams = { ...action.action_params };
            } else if (Array.isArray(action.action_params) && action.action_params[0]) {
              actionParams = {
                url: action.action_params[0].url ?? '',
                text: action.action_params[0].text ?? '',
              };
            } else {
              actionParams = { url: '', text: '' };
            }
          } else if (inputType === 'reminder') {
            // For reminder, action_params is an object with note (scheduled_time is set at execution time)
            if (action.action_params && typeof action.action_params === 'object' && !Array.isArray(action.action_params)) {
              actionParams = { ...action.action_params };
            } else if (Array.isArray(action.action_params) && action.action_params[0]) {
              actionParams = { ...action.action_params[0] };
            } else {
              actionParams = {
                note: '',
              };
            }
          } else {
            actionParams = [...action.action_params];
          }
        }
        return {
          ...action,
          action_params: actionParams,
        };
      });
      return {
        ...macro,
        actions: formattedActions,
      };
    },
    initNewMacro() {
      this.mode = 'CREATE';
      this.macro = {
        name: '',
        actions: [
          {
            action_name: 'assign_team',
            action_params: [],
          },
        ],
        visibility: 'global',
      };
    },
    getDefaultActionParams(actionName) {
      if (actionName === 'send_api_webhook') {
        return {
          url: '',
          method: 'POST',
          headers: '',
          payload: '',
          auto_execute: false,
        };
      }
      if (actionName === 'send_kivo_notification') {
        return { url: '', text: '' };
      }
      return [];
    },
    async saveMacro(macro) {
      try {
        const action = this.mode === 'EDIT' ? 'macros/update' : 'macros/create';
        let successMessage =
          this.mode === 'EDIT'
            ? this.$t('MACROS.EDIT.API.SUCCESS_MESSAGE')
            : this.$t('MACROS.ADD.API.SUCCESS_MESSAGE');
        let serializedMacro = JSON.parse(JSON.stringify(macro));
        serializedMacro.actions = actionQueryGenerator(serializedMacro.actions);
        await this.$store.dispatch(action, serializedMacro);
        this.showAlert(successMessage);
        this.$router.push({ name: 'macros_wrapper' });
      } catch (error) {
        this.showAlert(this.$t('MACROS.ERROR'));
      }
    },
  },
};
</script>
